import { zodResolver } from "@hookform/resolvers/zod";
import { isAxiosError } from "axios";
import moment from "moment";
import { useEffect, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { z } from "zod";
import { Button } from "@/components/design-system/button";
import { DatePicker } from "@/components/design-system/date-picker";
import FilterWithRadio from "@/components/design-system/FilterWithRadio";
import { Input } from "@/components/design-system/input";
import { bindPersonNameTextField } from "@/utils/formTextField";
import { trimmedRequiredPersonNameString } from "@/utils/zodString";
import { toCheckoutFormValuesFromStudent } from "../api/landingCheckout.mappers";
import { getStudentByEmailService } from "../api/landingCheckout.service";
import { LANDING_USE_MOCK } from "../config/landing.config";
import type { CheckoutFormValues } from "../types/landing.types";
import { LANDING_CHECKOUT_TYPOGRAPHY } from "../constants/landing.constants";
import LandingIcon from "./LandingIcon";

const GENDER_OPTIONS = [
  { value: "male", label: "Male" },
  { value: "female", label: "Female" },
  { value: "other", label: "Other" },
] as const;

const EMAIL_LOOKUP_DEBOUNCE_MS = 450;

const checkoutSchema = z.object({
  email: z.string().email("Invalid email address"),
  studentName: trimmedRequiredPersonNameString({
    requiredMessage: "Student name is required",
    min: 2,
    minMessage: "Student name must be at least 2 characters",
    max: 100,
  }),
  phone: z.string().regex(/^\d{10}$/, "Invalid phone number"),
  dob: z
    .string()
    .min(1, "Date of birth is required")
    .refine(
      (value) =>
        moment(value, ["YYYY-MM-DD", "DD-MM-YYYY", "DD/MM/YYYY"], true).isValid(),
      "Invalid date of birth"
    )
    .refine((value) => {
      const parsed = moment(
        value,
        ["YYYY-MM-DD", "DD-MM-YYYY", "DD/MM/YYYY"],
        true
      );
      return parsed.isSameOrBefore(moment(), "day");
    }, "Date of birth cannot be in the future"),
  gender: z.union(
    [z.literal("male"), z.literal("female"), z.literal("other")],
    {
      error: "Gender is required",
    }
  ),
});

interface CheckoutFormProps {
  onSubmit: (values: CheckoutFormValues) => Promise<void>;
  isSubmitting: boolean;
}

function isValidEmail(value: string): boolean {
  return z.string().email().safeParse(value.trim()).success;
}

function clearProfileFields(
  setValue: ReturnType<typeof useForm<CheckoutFormValues>>["setValue"]
) {
  setValue("studentName", "", { shouldValidate: false, shouldDirty: false });
  setValue("phone", "", { shouldValidate: false, shouldDirty: false });
  setValue("dob", "", { shouldValidate: false, shouldDirty: false });
  setValue("gender", undefined as unknown as CheckoutFormValues["gender"], {
    shouldValidate: false,
    shouldDirty: false,
  });
}

const UNLOCKED_FIELDS = {
  studentName: false,
  phone: false,
  dob: false,
  gender: false,
};

export default function CheckoutForm({ onSubmit, isSubmitting }: CheckoutFormProps) {
  const [isExistingStudent, setIsExistingStudent] = useState(false);
  const [isLookingUpEmail, setIsLookingUpEmail] = useState(false);
  const [lockedFields, setLockedFields] = useState({
    studentName: false,
    phone: false,
    dob: false,
    gender: false,
  });
  const lookupRequestId = useRef(0);

  const {
    register,
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { errors, isValid },
  } = useForm<CheckoutFormValues>({
    resolver: zodResolver(checkoutSchema),
    mode: "onChange",
    reValidateMode: "onChange",
    defaultValues: {
      email: "",
      studentName: "",
      phone: "",
      dob: "",
      gender: undefined as unknown as CheckoutFormValues["gender"],
    },
  });

  const emailValue = watch("email");
  const wasExistingRef = useRef(false);

  useEffect(() => {
    wasExistingRef.current = isExistingStudent;
  }, [isExistingStudent]);

  useEffect(() => {
    if (LANDING_USE_MOCK) return;

    const email = emailValue?.trim() ?? "";

    if (!isValidEmail(email)) {
      lookupRequestId.current += 1;
      setIsLookingUpEmail(false);
      if (wasExistingRef.current) {
        setIsExistingStudent(false);
        setLockedFields(UNLOCKED_FIELDS);
        clearProfileFields(setValue);
      }
      return;
    }

    // New lookup email — unlock immediately so stale readonly data is not shown
    if (wasExistingRef.current) {
      setIsExistingStudent(false);
      setLockedFields(UNLOCKED_FIELDS);
      clearProfileFields(setValue);
    }

    const requestId = ++lookupRequestId.current;
    const timer = window.setTimeout(async () => {
      setIsLookingUpEmail(true);
      try {
        const response = await getStudentByEmailService(email);
        if (requestId !== lookupRequestId.current) return;

        const student = response.data;
        if (!student) {
          setIsExistingStudent(false);
          setLockedFields(UNLOCKED_FIELDS);
          return;
        }

        const mapped = toCheckoutFormValuesFromStudent(student);
        if (!mapped) {
          setIsExistingStudent(false);
          setLockedFields(UNLOCKED_FIELDS);
          return;
        }

        setValue("studentName", mapped.studentName, {
          shouldValidate: true,
          shouldDirty: false,
        });
        setValue("phone", mapped.phone, {
          shouldValidate: true,
          shouldDirty: false,
        });
        setValue("dob", mapped.dob, {
          shouldValidate: true,
          shouldDirty: false,
        });
        if (mapped.gender) {
          setValue("gender", mapped.gender, {
            shouldValidate: true,
            shouldDirty: false,
          });
        } else {
          setValue(
            "gender",
            undefined as unknown as CheckoutFormValues["gender"],
            { shouldValidate: false, shouldDirty: false }
          );
        }
        setLockedFields(mapped.lockedFields);
        setIsExistingStudent(true);
      } catch (error) {
        if (requestId !== lookupRequestId.current) return;

        // 404 / not found → new email, keep form editable (do not wipe typed values)
        if (isAxiosError(error) && error.response?.status === 404) {
          setIsExistingStudent(false);
          setLockedFields(UNLOCKED_FIELDS);
          return;
        }

        setIsExistingStudent(false);
        setLockedFields(UNLOCKED_FIELDS);
      } finally {
        if (requestId === lookupRequestId.current) {
          setIsLookingUpEmail(false);
        }
      }
    }, EMAIL_LOOKUP_DEBOUNCE_MS);

    return () => {
      window.clearTimeout(timer);
    };
  }, [emailValue, setValue]);

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
      <Input
        label="Email Address"
        type="email"
        placeholder="Enter Email Address"
        errorMessage={errors.email?.message}
        variant={errors.email ? "error" : "default"}
        helperText={
          isLookingUpEmail
            ? "Looking up student details…"
            : isExistingStudent
              ? "Existing student found. Filled details are read-only; complete any missing fields."
              : "This email address will be your username to access the course."
        }
        {...register("email")}
        required
      />

      <Controller
        name="studentName"
        control={control}
        render={({ field }) => (
          <Input
            label="Student Name"
            placeholder="Enter Student Name"
            errorMessage={errors.studentName?.message}
            variant={errors.studentName ? "error" : "default"}
            readOnly={lockedFields.studentName}
            required
            {...bindPersonNameTextField(field)}
          />
        )}
      />

      <div className="grid grid-cols-1 gap-5 md:grid-cols-2 md:gap-4">
        <Controller
          name="dob"
          control={control}
          render={({ field }) => (
            <DatePicker
              id="checkout-dob"
              label="Date of Birth"
              placeholder="Select Date of Birth"
              value={field.value}
              onChange={(date) => field.onChange(date)}
              maxDate={moment().format("YYYY-MM-DD")}
              error={errors.dob?.message}
              required
              showAbove={false}
              readOnly={lockedFields.dob}
            />
          )}
        />

        <Controller
          name="gender"
          control={control}
          render={({ field }) => (
            <FilterWithRadio
              id="checkout-gender"
              label="Gender"
              placeholder="Select Gender"
              options={[...GENDER_OPTIONS]}
              value={field.value ?? ""}
              onChange={field.onChange}
              error={errors.gender?.message}
              required
              fieldStyle
              showAbove={false}
              disabled={lockedFields.gender}
            />
          )}
        />
      </div>

      <Input
        label="Phone Number"
        placeholder="Enter Phone Number"
        errorMessage={errors.phone?.message}
        variant={errors.phone ? "error" : "default"}
        readOnly={lockedFields.phone}
        {...register("phone")}
        required
      />

      <div className="flex flex-col gap-4 pt-2 md:flex-row md:items-center">
        <Button
          type="submit"
          variant="primary"
          disabled={isSubmitting || isLookingUpEmail || !isValid}
          className={LANDING_CHECKOUT_TYPOGRAPHY.submitButton}
        >
          {isSubmitting ? "Processing..." : "Pay Now"}
        </Button>
        <span className={LANDING_CHECKOUT_TYPOGRAPHY.securePayment}>
          <LandingIcon name="lock" className="h-4 w-4" />
          Secured SSL Encrypted Payment
        </span>
      </div>
    </form>
  );
}
